/** 通用工具函数（无外部依赖，可在浏览器与 Node 测试环境使用） */

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/**
 * 极简 createElement 辅助
 * el('div', { class: 'x', onclick: fn }, ['文本', childNode])
 */
export function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'html') node.innerHTML = v;
    else if (k === 'text') node.textContent = v;
    else if (k === 'dataset') Object.assign(node.dataset, v);
    else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2).toLowerCase(), v);
    else node.setAttribute(k, v);
  }
  (Array.isArray(children) ? children : [children]).forEach(c => {
    if (c === null || c === undefined || c === false) return;
    node.appendChild(typeof c === 'string' || typeof c === 'number'
      ? document.createTextNode(String(c)) : c);
  });
  return node;
}

export const uid = (prefix = 'id') =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** 稳定的内容指纹（用于导入去重），非加密用途 */
export function hash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const sample = (arr, n) => shuffle(arr).slice(0, n);
export const pick = arr => arr[Math.floor(Math.random() * arr.length)];
export const uniq = arr => Array.from(new Set(arr));
export const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

export const nowISO = () => new Date().toISOString();
export const todayKey = () => new Date().toISOString().slice(0, 10);

export function fmtDate(iso, withTime = false) {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = n => String(n).padStart(2, '0');
  const base = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return withTime ? `${base} ${p(d.getHours())}:${p(d.getMinutes())}` : base;
}

export function fmtDuration(ms) {
  if (!ms || ms < 0) return '0秒';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}秒`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m}分${r}秒` : `${m}分`;
  return `${Math.floor(m / 60)}小时${m % 60}分`;
}

/** 距离今天的天数（负数表示未来） */
export function daysAgo(iso) {
  if (!iso) return Infinity;
  const a = new Date(iso); a.setHours(0, 0, 0, 0);
  const b = new Date(); b.setHours(0, 0, 0, 0);
  return Math.round((b - a) / 86400000);
}

/** 英文答案归一化：去首尾标点、折叠空格、小写、统一弯引号 */
export function normalizeEn(s) {
  return String(s ?? '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[.,!?;:]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** 中文答案归一化 */
export function normalizeZh(s) {
  return String(s ?? '').replace(/[\s,，。、；;：:！!？?（）()]/g, '').trim();
}

export function tokenize(en) {
  return String(en || '').replace(/([.,!?;:])/g, ' $1 ').split(/\s+/).filter(Boolean);
}

export function debounce(fn, wait = 250) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

export function downloadJSON(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = el('a', { href: url, download: filename });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** 极简 toast（由 components/toast.js 挂载容器） */
export function toast(msg, type = 'info', ms = 2200) {
  let host = document.getElementById('toast-host');
  if (!host) {
    host = el('div', { id: 'toast-host' });
    document.body.appendChild(host);
  }
  const t = el('div', { class: `toast toast-${type}`, text: msg });
  host.appendChild(t);
  setTimeout(() => { t.classList.add('out'); setTimeout(() => t.remove(), 300); }, ms);
}

/* --------------------------- 语音合成（TTS） --------------------------- */
/**
 * 语音合成要点（踩过的坑都写在这里，改之前先读）：
 *   1) **必须按语言挑 voice**。浏览器默认 voice 跟着系统语言走，
 *      中文系统上不加约束地 speak(英文) 会被中文语音引擎念出来，听起来完全不像英语。
 *   2) getVoices() 首次可能返回空数组（异步加载），要监听 voiceschanged 后重试，
 *      否则第一次点击经常没声音。
 *   3) iOS/Safari 要求首次播放在用户手势里触发；且在 speak() 里先 cancel() 更容易触发。
 *   4) Chrome 对长文本（>~200 字符）会在 15 秒左右截断，长句子切段播。
 *   5) 每次 new SpeechSynthesisUtterance 之后必须持有引用，否则可能被 GC 掉导致中途静音。
 */

const VOICE_CACHE = { en: null, zh: null, all: [], loaded: false };
const queue = [];              // 长文本分段播放队列
let speaking = false;

/** 规范化语言标签：'en' / 'en-US' / 'zh-CN' → 主语言 */
function primaryLang(lang) {
  return String(lang || 'en').toLowerCase().split(/[-_]/)[0];
}

/** 从 voice 列表里按语言挑最合适的一个 */
function pickVoice(voices, lang) {
  const want = String(lang || 'en-US').toLowerCase();
  const wantMain = primaryLang(want);
  const scoped = voices.filter(v => String(v.lang || '').toLowerCase().startsWith(wantMain));
  if (!scoped.length) return null;

  const score = v => {
    const vl = String(v.lang || '').toLowerCase().replace('_', '-');
    let s = 0;
    if (vl === want.replace('_', '-')) s += 100;      // 地区完全匹配
    if (vl.startsWith(want.slice(0, 5))) s += 40;     // en-US / zh-CN 前缀
    if (v.localService) s += 20;                      // 本地语音更稳、无网也能用
    // 常见高质量语音优先
    if (/female|samantha|karen|moira|ting|tingting|mei|xiaoxiao|google/i.test(v.name)) s += 12;
    if (/compact|robot|novelty/i.test(v.name)) s -= 30;
    return s;
  };
  return scoped.slice().sort((a, b) => score(b) - score(a))[0];
}

function refreshVoices() {
  if (!('speechSynthesis' in window)) return VOICE_CACHE;
  const all = speechSynthesis.getVoices() || [];
  if (!all.length) return VOICE_CACHE;
  VOICE_CACHE.all = all;
  VOICE_CACHE.loaded = true;
  VOICE_CACHE.en = pickVoice(all, 'en-US');
  VOICE_CACHE.zh = pickVoice(all, 'zh-CN');
  return VOICE_CACHE;
}

/**
 * 预加载语音列表（建议在设置页/首屏调用一次）
 * @returns {Promise<void>}
 */
export function preloadVoices() {
  if (!('speechSynthesis' in window)) return Promise.resolve();
  refreshVoices();
  if (VOICE_CACHE.loaded) return Promise.resolve();
  return new Promise(resolve => {
    let done = false;
    const finish = () => { if (done) return; done = true; refreshVoices(); resolve(); };
    speechSynthesis.addEventListener?.('voiceschanged', finish, { once: true });
    // 兜底：部分浏览器不触发 voiceschanged（平板上可能更慢）
    setTimeout(finish, 2500);
  });
}

/** 当前可用语音信息（设置页展示用） */
export function voiceInfo() {
  refreshVoices();
  return {
    supported: 'speechSynthesis' in window,
    english: VOICE_CACHE.en ? `${VOICE_CACHE.en.name} (${VOICE_CACHE.en.lang})` : '系统默认英语语音',
    chinese: VOICE_CACHE.zh ? `${VOICE_CACHE.zh.name} (${VOICE_CACHE.zh.lang})` : '系统默认中文语音',
    total: VOICE_CACHE.all.length,
  };
}

/** 把长文本切成 ≤180 字符的片段（按句、再按逗号切） */
function splitForTTS(text, max = 180) {
  const s = String(text || '').trim();
  if (s.length <= max) return [s];
  const parts = s.split(/(?<=[.!?;:])\s+/).filter(Boolean);
  const out = [];
  let buf = '';
  for (const p of parts) {
    if (!buf) { buf = p; continue; }
    if ((buf + ' ' + p).length <= max) buf += ' ' + p;
    else { out.push(buf); buf = p; }
  }
  if (buf) out.push(buf);
  // 单句仍超长 → 按逗号硬切
  return out.flatMap(x => (x.length <= max ? [x] : x.match(new RegExp(`.{1,${max}}`, 'g')) || [x]));
}

function speakChunk(text, lang, rate) {
  return new Promise(resolve => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;                       // 明确指定语言，绝不让浏览器猜
    u.rate = rate;
    u.pitch = primaryLang(lang) === 'zh' ? 1.05 : 1.0;
    const voice = primaryLang(lang) === 'zh'
      ? (VOICE_CACHE.zh || pickVoice(VOICE_CACHE.all, 'zh-CN'))
      : (VOICE_CACHE.en || pickVoice(VOICE_CACHE.all, 'en-US'));
    if (voice) u.voice = voice;          // 明确指定英文 voice，避免英文被中文引擎朗读
    u.onend = () => resolve();
    u.onerror = () => resolve();
    heldUtterances.push(u);          // 防 GC
    try { speechSynthesis.speak(u); } catch { resolve(); }
  });
}

const heldUtterances = [];

/**
 * 朗读文本（自动按语言选语音）
 * @param {string} text
 * @param {'en'|'zh'|'en-US'|'zh-CN'} lang 语言；英语传 'en-US'，中文传 'zh-CN'
 * @param {number} rate 语速 0.5~1.5
 * @returns {Promise<boolean>} 是否成功发起朗读
 */
export async function speak(text, lang = 'en-US', rate = 0.9) {
  if (!('speechSynthesis' in window) || !text) return false;
  try {
    // 平板（iOS Safari / Android Chrome）首次 getVoices() 常为空：
    // 空列表时先等语音加载完成（最多 2.5s），确保能挑到正确的英文 voice
    if (!VOICE_CACHE.loaded) {
      refreshVoices();
      if (!VOICE_CACHE.loaded) await preloadVoices();
    }
    const token = ++speakToken;
    try { speechSynthesis.cancel(); } catch { /* ignore */ }
    heldUtterances.length = 0;
    // Android Chrome：cancel() 之后立刻 speak 会被吞掉，稍等一拍
    await new Promise(r => setTimeout(r, 70));
    if (token !== speakToken) return false;   // 已被新的播放请求取代
    const chunks = splitForTTS(text);
    for (let i = 0; i < chunks.length; i++) {
      if (token !== speakToken) return false;
      await speakChunk(chunks[i], lang, rate);
    }
    return true;
  } catch { return false; }
}

let speakToken = 0;

/** 读英文（单词/句子/选项一律走这个，避免中文语音念英文） */
export const speakEn = (text, rate = 0.9) => speak(text, 'en-US', rate);
/** 读中文（词义、解释、提示） */
export const speakZh = (text, rate = 0.95) => speak(text, 'zh-CN', rate);

/** 停止朗读 */
export function stopSpeak() {
  if (!('speechSynthesis' in window)) return;
  try { speechSynthesis.cancel(); heldUtterances.length = 0; } catch { /* ignore */ }
}

/** 根据文本内容自动判断语言（含 CJK 视为中文） */
export function autoSpeak(text, rate = 0.9) {
  return /[\u4e00-\u9fff]/.test(String(text || '')) ? speakZh(text, rate) : speakEn(text, rate);
}

export const escapeHtml = s => String(s ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));
